import type { Team } from '@uefadigital/panenka';
import { PkIcon } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { memo } from 'react';

import { useCalendarServiceOptions, useCalendarServiceStore } from '../../store';
import { trackView } from '../../utils';

import { useTeams } from '@hooks/useTeams';
import { useWindowSize } from '@hooks/useWindowSize';
import TeamIdentifier from '@shared/Identifier/TeamIdentifier';
import ListFilterModal from '@shared/ListFilterModal';
import TeamsDisclaimer from '@shared/TeamsDisclaimer';
import { useTranslateFn } from '@shared/Translate';
import GoogleTagManagerHelper from '@tracking/gtm-helper';

import * as classes from './styles.module.scss';

const ButtonContent = memo(() => {
  const t = useTranslateFn();
  const { isMobile } = useWindowSize();
  return (
    <>
      {!isMobile && t('add_calendar')}
      <PkIcon className={clsx(!isMobile && 'pk-ml--xs')} name='actions-add-to-calendar' size={24} />
    </>
  );
});
ButtonContent.displayName = 'ButtonContent';

const ItemComponent = memo(
  ({
    entity: team,
    selected,
    onSelect,
  }: {
    entity: Team;
    onSelect: (selected: boolean) => void;
    selected: boolean;
  }) => {
    const handleSelect = (
      e: CustomEvent<{
        element: HTMLElement;
        selected: boolean;
      }>,
    ) => onSelect(e.detail.selected);

    return (
      <TeamIdentifier
        key={`team-${team.id}`}
        className={clsx('pk-py--xs', classes.identifier)}
        team={team}
        type='vertical'
        enableSelection
        showTeamCountry={false}
        selected={selected}
        onIdentifierSelected={handleSelect}
      />
    );
  },
);
ItemComponent.displayName = 'ItemComponent';

const teamsDisclaimer = (
  <div className='pk-pb--xl pk-pb-sm--xl2'>
    <TeamsDisclaimer />
  </div>
);

const TeamSelector = () => {
  const t = useTranslateFn();
  const { options } = useCalendarServiceOptions();
  const selectedTeams = useCalendarServiceStore((state) => state.selectedTeams);
  const setSelectedTeams = useCalendarServiceStore((state) => state.setSelectedTeams);

  const teams = useTeams(options);

  const handleSelect = (teams: Team[]) => {
    const teamIds = teams?.map((team) => team.id);
    setTimeout(() => {
      setSelectedTeams(teamIds);
    }, 1000);
    GoogleTagManagerHelper.event(
      'Click',
      `Select ${teamIds.length} calendar/s - ${teamIds.join()}`,
      'addtocalendar',
      { section_reference: 'addtocalendar', interaction: `Select ${teamIds.length} calendar/s - ${teamIds.join()}` },
      'add_to_calendar',
    );
  };

  const handleOpen = () => {
    trackView('selectTeams', 'team listing');
  };

  if (!teams?.length) return null;

  return (
    <ListFilterModal
      multiSelect
      onSelected={handleSelect}
      onModalOpen={handleOpen}
      entities={teams}
      selectedEntities={teams.filter((t) => selectedTeams.includes(t.id))}
      headerText={t('add_calendar')}
      title={t('add_calendar_title')}
      errorText={t('add_calendar_error')}
      applyBtnTag='teamsel_add_button'
      resetBtnTag='teamsel_clear_button'
      buttonClassName={clsx({
        [classes.button]: true,
        [classes.calendarContext]: options.calendarContext,
      })}
      buttonWidth={options.calendarContext ? 'auto' : null}
      ButtonContent={ButtonContent}
      ItemComponent={ItemComponent}
      subContentNote={window.showTeamsDisclaimer ? teamsDisclaimer : null}
    />
  );
};

export default memo(TeamSelector);
